import type { GameId } from '../../shared/types/ids';
import type { LaunchIdentityRecord } from '../../shared/types/launch-identity';
import type { ChildProfile } from '../../shared/types/profiles';
import type { SessionLaunchConfig } from '../../shared/types/session-launch';
import type { ThriveRepositories } from '../../storage/interfaces/repositories';
import type { ActivePlayer } from '../shell/services-context';
import { loadSessionLaunchDefaults, resolveSessionLaunchDefaults } from './launch-defaults';

/**
 * What sitting is this (D-054(2), Slice R)?
 *
 * `activePlayer` and `sessionLaunchConfig` are React state and deliberately stay that way — a
 * level chosen because a child is tired tonight is a property of the evening, not of the child.
 * But a reload emptied them both, and one of the things they carried *is* a durable fact: which
 * children were in the room. Without it the resume guard could not prove a saved mission belonged
 * to whoever was now at the screen, so a named or cooperative sitting started over.
 *
 * Both consumers call this — the stage that resumes the mission, and Home's "carry on" card — so
 * the offer and the decision cannot drift apart (the D-046(4) principle). It resolves an identity
 * and never decides whether a resume is allowed: that stays with `canResumeMissionProgress`, whose
 * per-slot refusals (D-036(4), PS2-8) are untouched and are handed a rebuilt roster to judge.
 *
 * **Why it is not an effect in `AppServicesProvider`.** Writing the restored values back into shell
 * state would re-run the stage's boot effect mid-mission (both are dependencies of it), which can
 * start two session records and race the snapshot. Resolving at the consumers costs one storage
 * read each and cannot restart anything.
 */

export interface ResolveSittingInput {
  /**
   * The game the caller is about to launch, or whose saved mission it is asking about.
   *
   * Optional because Home may be asking with no saved mission in hand at all — there is then no
   * game to disagree with, and the record is stale for a reason that has nothing to do with which
   * game it names.
   */
  gameId?: GameId;
  sessionLaunchConfig: SessionLaunchConfig | null;
  activePlayer: ActivePlayer | null;
}

export interface ResolvedSitting {
  config: SessionLaunchConfig;
  /**
   * Who is at the screen, as the shell understands it. `null` for a guest sitting — a guest keeps
   * no identity (D-025), and inventing a labelled one would put a name on screen nobody typed.
   */
  player: ActivePlayer | null;
  /** True only when this was rebuilt from a stored launch-identity record. */
  restored: boolean;
}

export async function resolveSitting(
  repositories: ThriveRepositories,
  input: ResolveSittingInput,
): Promise<ResolvedSitting> {
  // 1. A live sitting. Somebody set this up in this tab; storage has nothing to add.
  if (input.sessionLaunchConfig !== null) {
    return { config: input.sessionLaunchConfig, player: input.activePlayer, restored: false };
  }

  /**
   * 2. Somebody has said who is at the screen this session, and that choice wins.
   *
   * Without this rule a sibling who picks their own profile after a reload would be handed the
   * restored child's mission — the exact misattribution D-036(4) exists to prevent, and one the
   * pre-Slice-R sibling test could not reach because it never reloaded.
   */
  if (input.activePlayer !== null) {
    return {
      config: await loadSessionLaunchDefaults(repositories, input.activePlayer),
      player: input.activePlayer,
      restored: false,
    };
  }

  const defaults = async (): Promise<ResolvedSitting> => ({
    config: await loadSessionLaunchDefaults(repositories, null),
    player: null,
    restored: false,
  });

  const [record, snapshot] = await Promise.all([
    repositories.launchIdentity.get(),
    repositories.missionProgress.getProgress(),
  ]);

  // 3. Nothing recorded who launched: today's behaviour, unchanged. A guest's own mission still
  // resumes through the guest slot; a named or cooperative one is still refused rather than guessed.
  if (record === null) return defaults();

  const forget = async (): Promise<ResolvedSitting> => {
    try {
      await repositories.launchIdentity.clear();
    } catch {
      // A clear that could not be verified is reported by the repository now (S1-5) and stays in
      // the erasure backlog. It must not stop the launch: the decision here is already "do not
      // restore this sitting", and that decision is taken in memory, not in storage.
    }
    return defaults();
  };

  // An identity that outlived its mission, or one recorded for another game, describes nothing
  // this launch can carry on. Cleared rather than left to be re-examined on every future launch.
  if (snapshot === null) return forget();
  if (input.gameId !== undefined && record.gameId !== input.gameId) return forget();
  if (record.gameId !== snapshot.gameId) return forget();

  /**
   * A named seat is only an identity while the child it names still exists. A deleted profile is
   * not "some child" — resuming as one would write the rest of the mission's evidence against
   * nobody in particular, and hand its score to whoever is at the screen. Refuse, forget, start
   * fresh; never guess.
   */
  const profiles = await Promise.all(
    record.players.map((seat) =>
      seat.profileId === undefined
        ? Promise.resolve<ChildProfile | null>(null)
        : repositories.profiles.getChild(seat.profileId),
    ),
  );
  const namedSeatIsStale = (
    seat: LaunchIdentityRecord['players'][number],
    index: number,
  ): boolean =>
    !seat.isGuest &&
    // Gone. A deleted profile is not "some child".
    (profiles[index] == null ||
      // Or replaced: `saveChild` upserts on the caller's id, so the same id can name a different
      // child than the one who owned this sitting (S1-8). The stored `createdAt` is the
      // generation, and a mismatch is a different child — refuse, forget, start fresh.
      profiles[index]?.createdAt !== seat.profileCreatedAt);

  if (record.players.some(namedSeatIsStale)) return forget();

  return rebuild(repositories, record, profiles);
}

/**
 * The sitting, rebuilt: identity from the record, everything else re-derived from storage exactly
 * as any other launch would (D-054(2)'s "persist identity, not preferences").
 */
async function rebuild(
  repositories: ThriveRepositories,
  record: LaunchIdentityRecord,
  profiles: readonly (ChildProfile | null)[],
): Promise<ResolvedSitting> {
  const seat = record.players[0];
  const child = profiles[0] ?? null;
  if (seat === undefined) {
    return {
      config: await loadSessionLaunchDefaults(repositories, null),
      player: null,
      restored: false,
    };
  }

  const player: ActivePlayer | null =
    seat.isGuest || child === null
      ? null
      : {
          playerId: child.id,
          profileId: child.id,
          // The child's stored nickname, never a one-off in-game name: D-054(2) allows profile ids
          // only, and "no names beyond what profiles already store".
          label: child.nickname,
          band: seat.band,
          isGuest: false,
          /**
           * The generation this sitting was launched for, taken from the **record** (P6R17-1).
           *
           * A restored sitting carries its identity forward exactly as a live one does. The record
           * is what was chosen; the profile beside it has already been proved to be that same
           * generation by `namedSeatIsStale` above, so this is the carried answer rather than a
           * fresh read standing in for one.
           */
          ...(seat.profileCreatedAt === undefined
            ? {}
            : { profileCreatedAt: seat.profileCreatedAt }),
        };

  const family = await repositories.profiles.getFamily();
  const base = resolveSessionLaunchDefaults({ activePlayer: player, child, family });
  const second = record.players[1];
  const secondChild = profiles[1] ?? null;

  return {
    config: {
      ...base,
      playerMode: record.playerMode,
      // The band the sitting was actually played at, which the snapshot's roster carries too.
      band: seat.band,
      ...(second === undefined
        ? {}
        : {
            secondPlayer: {
              ...(secondChild === null
                ? {}
                : {
                    profileId: secondChild.id,
                    label: secondChild.nickname,
                    // Carried from the record, like the first seat's (P6R17-1).
                    ...(second.profileCreatedAt === undefined
                      ? {}
                      : { profileCreatedAt: second.profileCreatedAt }),
                  }),
              band: second.band,
            },
          }),
    },
    player,
    restored: true,
  };
}
